import { TripCurrentLocationModel, TripDetailCalendarDto, TripDirectionDto, TripLocationDto } from '@/__generated';
import { Polyline } from '@/components/map/Polyline';
import { EnvConfig, ROW_GUTTER } from '@/config';
import { apiSdk } from '@/httpClient';
import { Col, Row, Spin } from 'antd';
import React from 'react';
import { AdvancedMarker, APIProvider, InfoWindow, Map, Marker, Pin, useMap } from '@vis.gl/react-google-maps';
import { BusIcon } from '@/assets/icons/entities/_';

type Props = {
  trip: TripDetailCalendarDto;
};
const MapFitControl = ({ tripLocations }: { tripLocations: TripLocationDto[] }) => {
  const map = useMap();
  React.useEffect(() => {
    if (tripLocations) {
      const bounds = new google.maps.LatLngBounds();
      tripLocations.forEach(location => {
        if (location) {
          bounds.extend({ lat: location.latitude || 0, lng: location.longitude || 0 });
        }
      });
      map?.fitBounds(bounds, 10);
    }
  }, [tripLocations]);
  return null;
};

export default function MapTab({ trip }: Props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [tripLocations, setTripLocations] = React.useState<TripLocationDto[]>([]);
  const [segments, setSegments] = React.useState<{ from: TripLocationDto; to: TripLocationDto; locations: TripLocationDto[] }[]>([]);

  const [overViewDirections, setOverviewDirections] = React.useState<Array<TripDirectionDto>>([]);
  const [currentLocation, setCurrentLocation] = React.useState<TripCurrentLocationModel>();

  React.useEffect(() => {
    if (trip.id) {
      apiSdk.TripLocationService.getTripDetailsDirections({ tripDetailId: trip.id || '' }).then(res => {
        setOverviewDirections(res.tripDirections || []);
      });
      apiSdk.TripLocationService.getTripDetailsLocations({ tripDetailId: trip.id || '' })
        .then(res => {
          setTripLocations(res.locations || []);
        })
        .finally(() => {
          setIsLoading(false);
        });
      apiSdk.TripLocationService.getTripCurrentLocation({ tripDetailId: trip.id || '' })
        .then(res => {
          setCurrentLocation(res || undefined);
        })
        .catch(() => {
          setCurrentLocation(undefined);
        });
    }
  }, [trip.id]);

  React.useEffect(() => {
    let _segments: { from: TripLocationDto; to: TripLocationDto; locations: TripLocationDto[] }[] = [];
    let indexOfStations = tripLocations.filter(x => x.stationId).map(x => x.order);
    for (var i = 0; i < indexOfStations.length - 1; i++) {
      let fromIndex = indexOfStations[i] || 0;
      let toIndex = indexOfStations[i + 1] || 0;
      _segments.push({
        from: tripLocations.find(x => x.order === fromIndex) as any,
        to: tripLocations.find(x => x.order === toIndex) as any,
        locations: tripLocations.filter(x => (x.order || 0) > fromIndex && (x.order || 0) < toIndex) || [],
      });
    }
    setSegments(_segments);
  }, [tripLocations]);

  const lastLocation = React.useMemo(() => {
    return tripLocations.at(-1);
  }, [tripLocations]);

  return (
    <Spin spinning={isLoading}>
      <div>
        <APIProvider apiKey={EnvConfig.googleMapKey || ''} onLoad={() => console.log('Maps API has loaded.')}>
          <Row gutter={ROW_GUTTER} id="base-trip-map">
            <Col md={24}>
              <div style={{ width: '100%', height: window.innerHeight - 200 }}>
                <Map mapId={trip.id} defaultZoom={13} style={{ width: '100%', height: '100%' }} gestureHandling="greedy" disableDefaultUI={true}>
                  {overViewDirections.map((direction, index) => {
                    return direction.directionPreview ? (
                      <Polyline key={index} encodedPath={direction.directionPreview} strokeColor="#f5222d" strokeOpacity={0.6} strokeWeight={5} />
                    ) : null;
                  })}
                  {segments.map(({ from, locations }) => {
                    return (
                      <>
                        {from ? <Marker label={from.name} position={{ lat: from.latitude || 0, lng: from.longitude || 0 }} /> : null}
                        {locations?.map(l => {
                          return (
                            <AdvancedMarker position={{ lat: l.latitude || 0, lng: l.longitude || 0 }}>
                              <Pin background={'#ecffed'} borderColor={'#1e89a1'} glyphColor={'#0f677a'}></Pin>
                              <InfoWindow>123123</InfoWindow>
                            </AdvancedMarker>
                          );
                        })}
                      </>
                    );
                  })}
                  {lastLocation ? (
                    <Marker label={lastLocation.name} position={{ lat: lastLocation.latitude || 0, lng: lastLocation.longitude || 0 }} />
                  ) : null}
                  {currentLocation ? (
                    <AdvancedMarker position={{ lat: currentLocation.latitude || 0, lng: currentLocation.longitude || 0 }}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          background: '#DFFF00',
                          borderRadius: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid #cccccc',
                        }}
                      >
                        <BusIcon width={14} />
                      </div>
                    </AdvancedMarker>
                  ) : null}
                  <MapFitControl tripLocations={tripLocations} />
                </Map>
              </div>
            </Col>
          </Row>
        </APIProvider>
      </div>
    </Spin>
  );
}
